import type { DocMigration } from "../docMigration";

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_407: DocMigration = {
  maxSchemaVersion: "0.0.407",
  description: "Set prop attribute type to FURNITURE based on prop asset name and add new prop categories",
  apply(json: any) {
    // set prop category
    for (const settingId in json.settings) {
      const setting = json.settings[settingId];

      if (setting.type === "EXTERIOR_PROP_SETTINGS" || setting.type === "INTERIOR_PROP_SETTINGS") {
        const { propTypes } = setting;
        for (const propType in propTypes) {
          const propAttributes = propTypes[propType];

          if (!propAttributes) {
            continue;
          }

          propAttributes.category = propAttributes.takeoffCategory;
          delete propAttributes.takeoffCategory;

          propAttributes.showOnElevationsAndPerspectives = propAttributes.isArchitecturalProp;
          delete propAttributes.isArchitecturalProp;

          propAttributes.useExteriorPalette = propAttributes.forceOnExteriorViews;
          delete propAttributes.forceOnExteriorViews;

          propAttributes.excludeFromFoundationPlanDimensions = propAttributes.drawOnlyFoundation;
          delete propAttributes.drawOnlyFoundation;

          propAttributes.hideTagOnElectricalPlan = propAttributes.drawOnlyElectrical;
          delete propAttributes.drawOnlyElectrical;

          if (propAttributes.category === "NONE") {
            propAttributes.category = "OTHER";
          }

          if (propAttributes.category === "APPLIANCES" || propAttributes.category === "FIXTURES") {
            continue;
          }

          if (propAttributes.propAssetId && furniturePropAssetIdToName[propAttributes.propAssetId.toString()]) {
            propAttributes.category = "FURNITURE";
          }

          if (propAttributes.category === undefined) {
            propAttributes.category = "OTHER";
          }
        }
      }
    }

    for (const viewTemplateId in json.viewTemplates) {
      replacePropSheetGeometryCategory(json.viewTemplates[viewTemplateId]);
    }

    for (const effectId in json.effects) {
      replacePropSheetGeometryCategory(json.effects[effectId]);
    }

    replaceViewFilterProps(json.planViews);
    replaceViewFilterProps(json.view3Ds);
    replaceViewFilterProps(json.elevationViews);
    replaceViewFilterProps(json.sectionViews);

    return json;
  },
};

export default migration_0_0_407;

function replaceViewFilterProps(viewTable: { [key: string]: any }) {
  for (const viewId in viewTable) {
    const view = viewTable[viewId];

    if (!view?.viewFilter) {
      continue;
    }

    const viewFilter = view.viewFilter;

    if (viewFilter.props) {
      viewFilter.propAppliances = true;
      viewFilter.propFixtures = true;
      viewFilter.propFurniture = true;
      viewFilter.propOther = true;

      delete viewFilter.props;
    }
  }
}

function replacePropSheetGeometryCategory({ categories }: { categories: string[] }) {
  const propCategoryIndex = categories.findIndex((column) => column === "PROPS");

  if (propCategoryIndex === -1) {
    return;
  }

  categories.splice(propCategoryIndex, 1, "PROP_APPLIANCES", "PROP_FIXTURES", "PROP_OTHER");

  // sanitize the categories array to ensure there are no references to PROPS
  categories = categories.filter((category) => category !== "PROPS");
}

const furniturePropAssetIdToName = {
  "1323": "Bed_Bed_CaliforniaKing_01",
  "1324": "Bed_Bed_FullXL_01",
  "1325": "Bed_Bed_TwinXL_01",
  "1345": "Dining_Dine_10Long_96x40_01",
  "1346": "Dining_Dine_6Long_60x40_01",
  "1347": "Dining_Dine_8Long_84x40_01",
  "1374": "Living_Seat_CornerSofa_01",
  "1721": "Living_Table_Coffee_Square_3x3_01",
  "187": "Bed_Bed_Full_01",
  "188": "Bed_Bed_King_01",
  "189": "Bed_Bed_Queen_01",
  "190": "Bed_Bed_Twin_01",
  "191": "Bed_Dresser_01",
  "192": "Bed_Dresser_Chest_01",
  "193": "Bed_Dresser_Mirror_01",
  "194": "Bed_Dresser_Nightstand_01",
  "205": "Deck_Dine_4SquareUmbrella_01",
  "206": "Deck_Dine_4Square_01",
  "207": "Deck_Dine_6LongUmbrella_01",
  "208": "Deck_Dine_6Long_01",
  "209": "Deck_Dine_6PicnicUmbrella_01",
  "210": "Deck_Firepit_Round_01",
  "214": "Deck_Seat_Adirondack_01",
  "215": "Deck_Seat_Bench_01",
  "216": "Deck_Seat_Lounge_01",
  "217": "Deck_Seat_Rocking_01",
  "218": "Deck_Seat_Single_01",
  "219": "Deck_Table_4Square_01",
  "220": "Deck_Table_6Long_01",
  "221": "Deck_Table_6Picnic_01",
  "222": "Deck_Table_Side_01",
  "223": "Deck_Umbrella_9ft_01",
  "224": "Dining_Cabinet_Buffet_01",
  "225": "Dining_Cabinet_Hutch_01",
  "226": "Dining_Dine_4Long_01",
  "227": "Dining_Dine_4Round_01",
  "228": "Dining_Dine_4SquareTall_01",
  "2281": "Bed_Dresser_Nightstand_30_01",
  "229": "Dining_Dine_4Square_01",
  "230": "Dining_Dine_6LongBench_01",
  "231": "Dining_Dine_6Long_01",
  "232": "Dining_Dine_6Round_01",
  "233": "Dining_Dine_8LongBench_01",
  "234": "Dining_Dine_8Long_01",
  "235": "Dining_Dine_8Long_1",
  "236": "Dining_Seat_Dining6Bench_01",
  "237": "Dining_Seat_Dining8Bench_01",
  "238": "Dining_Seat_DiningTall_01",
  "239": "Dining_Seat_Dining_01",
  "2392": "Living_TV_65WallSlim_01",
  "2393": "Living_TV_75WallSlim_01",
  "2394": "Living_TV_85WallSlim_01",
  "2395": "Living_Speaker_Ceiling_Circular_01",
  "2396": "Living_Speaker_Wall_Rectangular_01",
  "240": "Dining_Seat_StoolBar_01",
  "241": "Dining_Seat_StoolCounter_01",
  "242": "Dining_Table_4Long_01",
  "243": "Dining_Table_4Round_01",
  "244": "Dining_Table_4SquareTall_01",
  "2443": "Bed_Bed_Lofted_01",
  "2444": "Bed_Dresser_Armoire_01",
  "2445": "Bed_Bed_Bunk_01",
  "2446": "Bed_Bench_Full_01",
  "2447": "Bed_Bench_King_01",
  "2448": "Bed_Bench_Queen_01",
  "2449": "Bed_Bench_Twin_01",
  "245": "Dining_Table_4Square_01",
  "2450": "Dining_Cabinet_Hutch_Glass_01",
  "2453": "Living_Table_Sofa_01",
  "2455": "Living_Seat_LinenBench_01",
  "2456": "Living_Storage_Ottoman_Round_01",
  "246": "Dining_Table_6Long_01",
  "247": "Dining_Table_6Round_01",
  "248": "Dining_Table_8Long_01",
  "2526": "Bed_Bed_FullXL_NoHeadboard_01",
  "2527": "Bed_Bed_Full_NoHeadboard_01",
  "2528": "Bed_Bed_King_NoHeadboard_01",
  "2529": "Bed_Bed_Queen_NoHeadboard_01",
  "2530": "Bed_Bed_TwinXL_NoHeadboard_01",
  "2531": "Bed_Bed_Twin_NoHeadboard_01",
  "2534": "Bed_Bed_CaliforniaKing_NoHeadboard_01",
  "2541": "Living_Storage_Ottoman_Rectangular_01",
  "2802": "Dining_Dine_4Long_60x40_01",
  "53": "Arch_GarageTrellis_10_01",
  "54": "Arch_GarageTrellis_16_01",
  "55": "Arch_GarageTrellis_17_01",
  "56": "Arch_GarageTrellis_18_01",
  "57": "Arch_GarageTrellis_Capital_08f11_01",
  "58": "Arch_GarageTrellis_Capital_08f8_01",
  "59": "Arch_GarageTrellis_Capital_09f7_01",
  "60": "Arch_GarageTrellis_Capital_16f11_01",
  "61": "Arch_GarageTrellis_Capital_16f8_01",
  "62": "Arch_GarageTrellis_Capital_17f7_01",
  "717": "Garage_Car_Sedan_01",
  "718": "Garage_SUV_Crossover_01",
  "719": "Garage_Truck_Pickup_01",
  "823": "Living_Cabinet_TV_01",
  "824": "Living_Seat_ArmChairChaise_01",
  "825": "Living_Seat_ArmChair_01",
  "826": "Living_Seat_LoveSeat_01",
  "827": "Living_Seat_Sofa3ChaiseL_01",
  "828": "Living_Seat_Sofa3Straight_01",
  "829": "Living_Table_Coffee_01",
  "830": "Living_Table_EndRectangle_01",
  "831": "Living_Table_EndSquare_01",
  "832": "Living_TV_65Wall_01",
  "833": "Living_TV_65_01",
  "836": "Office_Bookcase_2Single_01",
  "837": "Office_Bookcase_3Single_01",
  "838": "Office_Bookcase_4Single_01",
  "839": "Office_Bookcase_5Single_01",
  "840": "Office_Bookcase_6Single_01",
  "841": "Office_Chair_Roll_01",
  "842": "Office_DeskSet_1Cabinet_01",
  "843": "Office_DeskSet_2Cabinet_01",
  "844": "Office_Desk_1Cabinet_01",
  "845": "Office_Desk_2Cabinet_01",
  "874": "Utility_GarageDoor_Opener_01",
};
